import { useState, useEffect, useRef } from 'react';
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import FilterModal from '../FilterModal';
import { CompetenceFacetCount } from 'utils/wemble-api.generated';
import { useGetLevelOfSkillsQuery } from 'utils/wemble-api';

export interface CompetenceSelection {
  competenceId: string;
  minLevel?: number;
}

interface CompetenceModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (selections: CompetenceSelection[], operator: 'and' | 'or') => void;
  competences: CompetenceFacetCount[];
  initialSelected: CompetenceSelection[];
  initialOperator?: 'and' | 'or';
}

const useStyles = makeStyles({
  searchInput: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    fontSize: 13,
    marginBottom: 8,
    outline: 'none',
    boxSizing: 'border-box',
  },
  selectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    minHeight: 18,
  },
  selectionCount: {
    fontSize: 12,
    color: '#999',
  },
  clearButton: {
    fontSize: 12,
    color: '#4A9B6F',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  groupHeader: {
    fontSize: 11,
    fontWeight: 600,
    color: '#999',
    padding: '10px 12px 4px',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 8,
    cursor: 'pointer',
    marginBottom: 1,
    userSelect: 'none',
    transition: 'background-color 0.1s',
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    color: 'white',
    fontWeight: 700,
    flexShrink: 0,
  },
  levelRow: {
    padding: '4px 12px 6px 40px',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    flexWrap: 'wrap',
  },
  levelLabel: {
    fontSize: 11,
    color: '#888',
    marginRight: 2,
  },
  levelPill: {
    padding: '2px 8px',
    borderRadius: 10,
    border: 'none',
    fontSize: 11,
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    padding: 20,
    textAlign: 'center',
    color: '#999',
    fontSize: 13,
  },
  operatorToggle: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginRight: 'auto',
  },
  operatorButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  operatorButton: {
    padding: '4px 10px',
    borderRadius: 8,
    border: 'none',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  operatorDescription: {
    fontSize: 10,
    color: '#aaa',
    maxWidth: 280,
    lineHeight: 1.3,
  },
});

const CompetenceModal = ({ visible, onClose, onSave, competences, initialSelected, initialOperator = 'and' }: CompetenceModalProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selected, setSelected] = useState<CompetenceSelection[]>(initialSelected);
  const [search, setSearch] = useState('');
  const [operator, setOperator] = useState<'and' | 'or'>(initialOperator);
  const { data: levels } = useGetLevelOfSkillsQuery();

  const sortedLevels = levels ? [...levels].filter(l => l.level != null).sort((a, b) => (a.level ?? 0) - (b.level ?? 0)) : [];

  useEffect(() => {
    if (visible) {
      setSelected(initialSelected);
      setSearch('');
      setOperator(initialOperator);
    }
  }, [visible]);

  // Lock sort order on open
  const sortOrderRef = useRef<string[]>([]);
  useEffect(() => {
    if (visible) {
      const selectedIds = new Set(initialSelected.map(s => s.competenceId));
      sortOrderRef.current = [...competences]
        .sort((a, b) => {
          const aSelected = selectedIds.has(a._id) ? -1 : 0;
          const bSelected = selectedIds.has(b._id) ? -1 : 0;
          if (aSelected !== bSelected) return aSelected - bSelected;
          return b.totalCount - a.totalCount;
        })
        .map(c => c._id);
    }
  }, [visible]);

  // Group by direct parent. Header shows "Grandparent - Parent" when grandparent exists.
  const childCompetences = competences.filter(c => c.parent);
  const hasHierarchy = childCompetences.length > 0;
  const topLevelCompetences = competences.filter(c => !c.parent);

  const parentGroups = (() => {
    if (!hasHierarchy) return null;
    const groupMap = new Map<string, { groupLabel: string; children: CompetenceFacetCount[] }>();
    for (const child of childCompetences) {
      const pid = child.parent!;
      if (!groupMap.has(pid)) {
        const parentName = (child as any).parentName || pid;
        const grandparentName = (child as any).grandparentName;
        const label = grandparentName ? `${grandparentName} - ${parentName}` : parentName;
        groupMap.set(pid, { groupLabel: label, children: [] });
      }
      groupMap.get(pid)!.children.push(child);
    }
    return Array.from(groupMap.values());
  })();

  const isSelected = (id: string) => selected.some(s => s.competenceId === id);
  const getSelection = (id: string) => selected.find(s => s.competenceId === id);

  const toggle = (competence: CompetenceFacetCount) => {
    if (isSelected(competence._id)) {
      setSelected(prev => prev.filter(s => s.competenceId !== competence._id));
    } else {
      setSelected(prev => [...prev, { competenceId: competence._id }]);
    }
  };

  const setMinLevel = (competenceId: string, level: number | undefined) => {
    setSelected(prev => prev.map(s =>
      s.competenceId === competenceId ? { ...s, minLevel: level } : s
    ));
  };

  const searchLower = search.toLowerCase();
  const matchesSearch = (c: CompetenceFacetCount) =>
    isSelected(c._id) || !search ||
    c.name.toLowerCase().includes(searchLower) ||
    ((c as any).parentName || '').toLowerCase().includes(searchLower) ||
    ((c as any).grandparentName || '').toLowerCase().includes(searchLower);

  const filteredCompetences = competences
    .filter(matchesSearch)
    .sort((a, b) => sortOrderRef.current.indexOf(a._id) - sortOrderRef.current.indexOf(b._id));

  const filteredTopLevel = topLevelCompetences.filter(matchesSearch);
  const filteredGroups = parentGroups
    ? parentGroups
        .map(g => ({ ...g, children: g.children.filter(matchesSearch) }))
        .filter(g => g.children.length > 0)
    : [];

  const handleSave = () => {
    onSave(selected, operator);
    onClose();
  };

  const Checkbox = ({ checked }: { checked: boolean }) => (
    <div
      className={classes.checkbox}
      style={{
        border: checked ? 'none' : '2px solid #ccc',
        backgroundColor: checked ? '#4A9B6F' : 'white',
      }}
    >
      {checked && '✓'}
    </div>
  );

  const LevelSelector = ({ competenceId, competence }: { competenceId: string; competence: CompetenceFacetCount }) => {
    const selection = getSelection(competenceId);
    if (!selection || sortedLevels.length === 0) return null;

    // The lowest level = "any level" (no filter). Higher levels = "this level or higher".
    const lowestLevel = sortedLevels[0]?.level;

    const getCumulativeCount = (minLevel: number) => {
      return competence.countsByLevel
        .filter(cl => cl.level != null && cl.level >= minLevel)
        .reduce((sum, cl) => sum + cl.count, 0);
    };

    // Treat no minLevel or minLevel equal to the lowest as "lowest selected"
    const effectiveMinLevel = selection.minLevel ?? lowestLevel;

    return (
      <div className={classes.levelRow}>
        <span className={classes.levelLabel}>Min:</span>
        {sortedLevels.map(level => {
          const isActive = effectiveMinLevel === level.level;
          const cumulativeCount = level.level != null ? getCumulativeCount(level.level) : 0;
          // Clicking the lowest level clears the minLevel (same as "any")
          const levelValue = level.level === lowestLevel ? undefined : (level.level ?? undefined);
          return (
            <button
              key={level._id}
              onClick={(e) => { e.stopPropagation(); setMinLevel(competenceId, levelValue); }}
              className={classes.levelPill}
              style={{
                backgroundColor: isActive ? '#4A9B6F' : '#f0f0f0',
                color: isActive ? 'white' : '#666',
              }}
            >
              {level.name}+{cumulativeCount ? ` (${cumulativeCount})` : ''}
            </button>
          );
        })}
      </div>
    );
  };

  const CompetenceRow = ({ competence, indent }: { competence: CompetenceFacetCount; indent?: boolean }) => {
    const checked = isSelected(competence._id);
    return (
      <div>
        <div
          onClick={() => toggle(competence)}
          className={classes.row}
          style={{
            padding: `6px 12px 6px ${indent ? 32 : 12}px`,
            backgroundColor: checked ? '#E8F5EE' : 'transparent',
          }}
          onMouseEnter={e => { if (!checked) e.currentTarget.style.backgroundColor = '#FAFAFA'; }}
          onMouseLeave={e => { if (!checked) e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Checkbox checked={checked} />
            <span style={{ fontSize: 13, color: '#333' }}>{competence.name}</span>
          </div>
          <span style={{ fontSize: 12, color: '#999', flexShrink: 0, marginLeft: 8 }}>{competence.totalCount}</span>
        </div>
        {checked && <LevelSelector competenceId={competence._id} competence={competence} />}
      </div>
    );
  };

  const OperatorToggle = () => {
    if (selected.length < 2) return null;
    return (
      <div className={classes.operatorToggle}>
        <div className={classes.operatorButtons}>
          <button
            className={classes.operatorButton}
            style={{ backgroundColor: operator === 'and' ? '#38566B' : '#f0f0f0', color: operator === 'and' ? 'white' : '#666' }}
            onClick={() => setOperator('and')}
          >
            ALL
          </button>
          <button
            className={classes.operatorButton}
            style={{ backgroundColor: operator === 'or' ? '#38566B' : '#f0f0f0', color: operator === 'or' ? 'white' : '#666' }}
            onClick={() => setOperator('or')}
          >
            ANY
          </button>
        </div>
        <span className={classes.operatorDescription}>
          {operator === 'and'
            ? <>Shows users with <b>ALL</b> of the selected competencies</>
            : <>Shows users with <b>ANY</b> of the selected competencies</>}
        </span>
      </div>
    );
  };

  return (
    <FilterModal
      visible={visible}
      onClose={onClose}
      onSave={handleSave}
      title="Competencies"
      description="Filter by competency and skill level"
      wide
      footerLeft={<OperatorToggle />}
    >
      <div>
        {competences.length > 5 && (
          <input
            autoFocus={!isMobile}
            type="text"
            placeholder="Search competencies..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={classes.searchInput}
          />
        )}

        <div className={classes.selectionHeader}>
          {selected.length > 0 ? (
            <>
              <span className={classes.selectionCount}>{selected.length} selected</span>
              <button onClick={() => setSelected([])} className={classes.clearButton}>Clear all</button>
            </>
          ) : (
            <span className={classes.selectionCount} style={{ color: '#ccc' }}>No competencies selected</span>
          )}
        </div>

        {hasHierarchy && parentGroups ? (
          <>
            {filteredTopLevel.map(competence => (
              <CompetenceRow key={competence._id} competence={competence} />
            ))}
            {filteredGroups.map(group => (
              <div key={group.groupLabel}>
                <div className={classes.groupHeader}>{group.groupLabel}</div>
                {group.children.map(child => (
                  <CompetenceRow key={child._id} competence={child} indent />
                ))}
              </div>
            ))}
          </>
        ) : (
          filteredCompetences.map(competence => (
            <CompetenceRow key={competence._id} competence={competence} />
          ))
        )}

        {filteredCompetences.length === 0 && (!hasHierarchy || (filteredTopLevel.length === 0 && filteredGroups.length === 0)) && (
          <div className={classes.emptyState}>
            {search ? 'No competencies match your search' : 'No competencies available'}
          </div>
        )}
      </div>
    </FilterModal>
  );
};

export default CompetenceModal;
